/**
 * TutorialUI - 教程弹窗组件
 * @description 可复用的教程展示组件，支持公式展示、跳过功能和开始按钮
 */

import Phaser from 'phaser';
import { FormulaDisplay } from './FormulaDisplay.ts';
import type { TutorialData } from '@/data/tutorials.ts';

// 设计系统颜色 Token
const COLORS = {
  bgOverlay: 0x1a1a2e,
  bgPanel: 0x2a2a4a,
  border: 0x4a4a6a,
  accent: 0x4ade80,
  textPrimary: '#ffffff',
  textSecondary: '#888888',
  textFormula: '#4ade80',
};

// 设计系统尺寸 Token
const SIZES = {
  panelWidth: 600,
  panelHeight: 280,
  buttonWidth: 120,
  buttonHeight: 40,
  skipButtonWidth: 80,
  padding: 20,
};

/**
 * TutorialUI 配置
 */
export interface TutorialUIConfig {
  /** 关卡编号 */
  levelNumber: number;
  /** 教程数据 */
  tutorialData: TutorialData;
  /** 点击开始回调 */
  onStart?: () => void;
  /** 点击跳过回调 */
  onSkip?: () => void;
}

/**
 * TutorialUI 类
 * 教程弹窗组件，包含标题、公式、说明和按钮
 */
export class TutorialUI {
  private scene: Phaser.Scene;
  private config: TutorialUIConfig;

  // UI 元素
  private container!: Phaser.GameObjects.Container;
  private panelBg!: Phaser.GameObjects.Graphics;
  private titleText!: Phaser.GameObjects.Text;
  private formulaDisplay!: FormulaDisplay;
  private contentTexts!: Phaser.GameObjects.Text[];
  private startButton!: Phaser.GameObjects.Graphics;
  private startLabel!: Phaser.GameObjects.Text;
  private skipButton!: Phaser.GameObjects.Graphics;
  private skipLabel!: Phaser.GameObjects.Text;

  // 状态
  private isVisible: boolean = true;

  constructor(scene: Phaser.Scene, config: TutorialUIConfig) {
    this.scene = scene;
    this.config = config;

    // 创建容器
    this.container = scene.add.container(400, 300);
    this.container.setDepth(1000); // 置于顶层

    // 创建 UI 元素
    this.createUI();
  }

  /**
   * 创建所有 UI 元素
   */
  private createUI(): void {
    const { tutorialData, levelNumber } = this.config;

    // 背景遮罩
    this.panelBg = this.scene.add.graphics();
    this.panelBg.fillStyle(COLORS.bgOverlay, 0.85);
    this.panelBg.fillRect(50, 100, SIZES.panelWidth + 100, SIZES.panelHeight + 100);
    this.container.add(this.panelBg);

    // 弹窗面板
    const panelGraphics = this.scene.add.graphics();
    panelGraphics.fillStyle(COLORS.bgPanel, 1);
    panelGraphics.fillRoundedRect(0, -SIZES.panelHeight / 2, SIZES.panelWidth, SIZES.panelHeight, 8);
    panelGraphics.lineStyle(2, COLORS.border);
    panelGraphics.strokeRoundedRect(0, -SIZES.panelHeight / 2, SIZES.panelWidth, SIZES.panelHeight, 8);
    this.container.add(panelGraphics);

    // 关卡标题
    this.titleText = this.scene.add.text(
      SIZES.panelWidth / 2,
      -SIZES.panelHeight / 2 + SIZES.padding,
      `📚 关卡${levelNumber}：${tutorialData.title}`,
      {
        fontSize: '20px',
        color: COLORS.textPrimary,
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);
    this.container.add(this.titleText);

    // 公式展示组件
    this.formulaDisplay = new FormulaDisplay(this.scene, {
      formula: tutorialData.formula,
      x: SIZES.panelWidth / 2,
      y: -SIZES.panelHeight / 2 + 60,
    });
    this.formulaDisplay.addToContainer(this.container);

    // 内容说明
    this.contentTexts = [];
    const contentLines = this.getContentLines();
    contentLines.forEach((line, i) => {
      const text = this.scene.add.text(
        SIZES.panelWidth / 2,
        -SIZES.panelHeight / 2 + 100 + i * 22,
        line,
        {
          fontSize: '14px',
          color: line.startsWith('•') ? COLORS.textSecondary : COLORS.textPrimary,
          fontFamily: 'Arial',
        }
      ).setOrigin(0.5);
      this.contentTexts.push(text);
      this.container.add(text);
    });

    // 开始按钮
    this.createStartButton();

    // 跳过按钮
    this.createSkipButton();
  }

  /**
   * 获取内容行（根据教程数据格式化）
   */
  private getContentLines(): string[] {
    const { tutorialData } = this.config;
    const lines: string[] = [];

    // 变量解释
    if (tutorialData.variables) {
      tutorialData.variables.forEach(v => {
        lines.push(`• ${v}`);
      });
    }

    // 目标说明
    if (tutorialData.goal) {
      lines.push('');
      lines.push(`🎯 目标：${tutorialData.goal}`);
    }

    // 操作提示
    if (tutorialData.hint) {
      lines.push('');
      lines.push(`💡 提示：${tutorialData.hint}`);
    }

    return lines;
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    const buttonX = SIZES.panelWidth / 2;
    const buttonY = SIZES.panelHeight / 2 - SIZES.padding - 10;

    this.startButton = this.scene.add.graphics();
    this.startButton.fillStyle(COLORS.accent, 1);
    this.startButton.fillRoundedRect(
      buttonX - SIZES.buttonWidth / 2,
      buttonY - SIZES.buttonHeight / 2,
      SIZES.buttonWidth,
      SIZES.buttonHeight,
      4
    );
    this.container.add(this.startButton);

    this.startLabel = this.scene.add.text(
      buttonX,
      buttonY,
      '开始挑战',
      {
        fontSize: '16px',
        color: COLORS.textPrimary,
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);
    this.container.add(this.startLabel);

    // 交互
    const hitArea = new Phaser.Geom.Rectangle(
      buttonX - SIZES.buttonWidth / 2,
      buttonY - SIZES.buttonHeight / 2,
      SIZES.buttonWidth,
      SIZES.buttonHeight
    );
    this.startButton.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    this.startButton.on('pointerdown', () => {
      this.hide();
      this.config.onStart?.();
    });

    this.startButton.on('pointerover', () => {
      this.startButton.clear();
      this.startButton.fillStyle(0x5aee90, 1);
      this.startButton.fillRoundedRect(
        buttonX - SIZES.buttonWidth / 2,
        buttonY - SIZES.buttonHeight / 2,
        SIZES.buttonWidth,
        SIZES.buttonHeight,
        4
      );
    });

    this.startButton.on('pointerout', () => {
      this.startButton.clear();
      this.startButton.fillStyle(COLORS.accent, 1);
      this.startButton.fillRoundedRect(
        buttonX - SIZES.buttonWidth / 2,
        buttonY - SIZES.buttonHeight / 2,
        SIZES.buttonWidth,
        SIZES.buttonHeight,
        4
      );
    });
  }

  /**
   * 创建跳过按钮
   */
  private createSkipButton(): void {
    const buttonX = SIZES.panelWidth - SIZES.skipButtonWidth / 2 - SIZES.padding;
    const buttonY = -SIZES.panelHeight / 2 + SIZES.padding + 5;

    this.skipButton = this.scene.add.graphics();
    this.skipButton.fillStyle(COLORS.border, 1);
    this.skipButton.fillRoundedRect(
      buttonX - SIZES.skipButtonWidth / 2,
      buttonY - 15,
      SIZES.skipButtonWidth,
      30,
      4
    );
    this.container.add(this.skipButton);

    this.skipLabel = this.scene.add.text(
      buttonX,
      buttonY,
      '跳过',
      {
        fontSize: '14px',
        color: COLORS.textSecondary,
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);
    this.container.add(this.skipLabel);

    // 交互
    const hitArea = new Phaser.Geom.Rectangle(
      buttonX - SIZES.skipButtonWidth / 2,
      buttonY - 15,
      SIZES.skipButtonWidth,
      30
    );
    this.skipButton.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    this.skipButton.on('pointerdown', () => {
      this.hide();
      this.config.onSkip?.();
    });

    this.skipButton.on('pointerover', () => {
      this.skipButton.clear();
      this.skipButton.fillStyle(0x5a5a7a, 1);
      this.skipButton.fillRoundedRect(
        buttonX - SIZES.skipButtonWidth / 2,
        buttonY - 15,
        SIZES.skipButtonWidth,
        30,
        4
      );
      this.skipLabel.setColor(COLORS.textPrimary);
    });

    this.skipButton.on('pointerout', () => {
      this.skipButton.clear();
      this.skipButton.fillStyle(COLORS.border, 1);
      this.skipButton.fillRoundedRect(
        buttonX - SIZES.skipButtonWidth / 2,
        buttonY - 15,
        SIZES.skipButtonWidth,
        30,
        4
      );
      this.skipLabel.setColor(COLORS.textSecondary);
    });
  }

  /**
   * 显示教程
   */
  show(): void {
    this.isVisible = true;
    this.container.setVisible(true);
  }

  /**
   * 隐藏教程
   */
  hide(): void {
    this.isVisible = false;
    this.container.setVisible(false);
  }

  /**
   * 销毁教程组件
   */
  destroy(): void {
    this.container.destroy();
    this.formulaDisplay.destroy();
  }

  /**
   * 获取可见状态
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }
}